import React, { useState, useEffect, useRef } from 'react';
import './LoginPage.css';
import { Link, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from './apiBaseUrl';
import { clearUserSession, compactUserForStorage } from './authSession';
import { pingKioskLogin } from './kioskPing';
import { FullPageLoader, useRouteEntryLoader, withRouteLoaderState } from './navigationLoader';

const preserveFirstProfilePic = (nextUser) => {
    const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
    const existingProfilePic = String(existingUser.profile_pic_url || '').trim();
    const nextProfilePic = String(nextUser?.profile_pic_url || '').trim();

    if (existingProfilePic && existingProfilePic !== nextProfilePic) {
        return { ...nextUser, profile_pic_url: existingProfilePic };
    }

    return nextUser;
};

const normalizeLoginErrorMessage = (message) => {
    const text = String(message || '').trim();
    const normalizedText = text.toLowerCase();

    if (normalizedText.includes('pan_lookup_unavailable')) {
        return 'PAN details unavailable. Please enter PAN to continue.';
    }

    if (normalizedText.includes('dob_required_for_crif')) {
        return 'Date of birth is required to verify PAN and continue.';
    }

    if (
        normalizedText.includes('mobile number is not associated with the given pan number')
        || (normalizedText.includes('mobile number') && normalizedText.includes('pan number'))
    ) {
        return 'This mobile number is not linked to the current profile.';
    }

    return text || 'Login failed.';
};

// Carousel and slides are intentionally commented out for LoginPage.
// import Carousel from './Carousel';
// import slide1 from './assets/slide1.png';
// import slide3 from './assets/slide3.png';
// import slide5 from './assets/slide5.png';
// const slides = [
//     {
//         image: slide3,
//         text: "Instant Loans for Salaried Professionals"
//     },
//     {
//         image: process.env.PUBLIC_URL + '/image3_corporate_final.png',
//         text: "Zero Paperwork for Salaried Employees"
//     },
//     {
//         image: slide5,
//         text: "Salary Credited? Get a Top-up instantly!"
//     },
//     {
//         image: slide1,
//         text: "Upgrade Your Lifestyle Today"
//     },
//     {
//         image: process.env.PUBLIC_URL + '/travling.jpg',
//         text: "Travel Now, Pay Later"
//     }
// ];

const LoginPage = () => {
    const VisibilityIcon = ({ visible }) => (
        <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            aria-hidden="true"
            focusable="false"
        >
            <path
                d="M2 12C4.4 7.8 8 5.7 12 5.7S19.6 7.8 22 12c-2.4 4.2-6 6.3-10 6.3S4.4 16.2 2 12Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <circle
                cx="12"
                cy="12"
                r="3"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
            />
            {visible ? null : (
                <path
                    d="M4 4l16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                />
            )}
        </svg>
    );

    const [formData, setFormData] = useState({
        mobile: '',
        mobileOtp: '',
        panNumber: '',
        consent: false,
        consentTerms: false
    });

    const [otpSent, setOtpSent] = useState({
        mobile: false
    });

    const [popup, setPopup] = useState({
        show: false,
        message: '',
        type: 'info',
        autoCloseMs: 0
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [otpCooldown, setOtpCooldown] = useState(0);
    const [location, setLocation] = useState(null);
    const [showSensitiveFields, setShowSensitiveFields] = useState({
        mobile: false,
        panNumber: false
    });
    const [showPanFallback, setShowPanFallback] = useState(false);
    const panFallbackRef = useRef(null);
    const [legalModal, setLegalModal] = useState({
        open: false,
        url: '',
        title: '',
        isLocal: false
    });
    const navigate = useNavigate();
    const entryLoaderMessage = useRouteEntryLoader('Opening page...');

    const persistLoginSession = (user, token) => {
        const mergedUser = preserveFirstProfilePic(user || {});
        localStorage.setItem('user', JSON.stringify(compactUserForStorage(mergedUser)));
        if (token) {
            localStorage.setItem('token', token);
        }
        return mergedUser;
    };

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const locStr = `${position.coords.latitude}, ${position.coords.longitude}`;
                    setLocation(locStr);
                },
                (error) => {
                    console.warn("Geolocation permission denied or error:", error.message);
                }
            );
        }
    }, []);

    const recordLead = async (mobile) => {
        if (!mobile || mobile.length !== 10) return;
        try {
            const baseUrl = getApiBaseUrl();
            await fetch(`${baseUrl}/api/record-lead`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mobile,
                    kioskId: localStorage.getItem('kiosk_id') || process.env.REACT_APP_KIOSK_ID || null
                })
            });
        } catch (err) {
            console.warn("Lead capture failed:", err);
        }
    };

    // Lead Capture Logic: Record if mobile is filled
    useEffect(() => {
        let timer;
        if (formData.mobile.length === 10) {
            // Immediate capture
            recordLead(formData.mobile);

            // Re-capture after 1 min if they are still on the page
            timer = setTimeout(() => {
                recordLead(formData.mobile);
            }, 60000);
        }
        return () => clearTimeout(timer);
    }, [formData.mobile]);


    useEffect(() => {
        if (!popup.show || !popup.autoCloseMs) return undefined;
        const timer = setTimeout(() => {
            setPopup((prev) => ({ ...prev, show: false, autoCloseMs: 0 }));
        }, popup.autoCloseMs);
        return () => clearTimeout(timer);
    }, [popup.autoCloseMs, popup.show]);

    useEffect(() => {
        if (otpCooldown <= 0) return undefined;
        const timer = setTimeout(() => {
            setOtpCooldown((prev) => Math.max(0, prev - 1));
        }, 1000);
        return () => clearTimeout(timer);
    }, [otpCooldown]);

    const isValidMobileNumber = (mobile) => /^[6-9]\d{9}$/.test(mobile);
    const isValidPanNumber = (panNumber) => /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(String(panNumber || '').trim().toUpperCase());

    const showPopup = (message, type = 'info', options = {}) => {
        setPopup({
            show: true,
            message,
            type,
            autoCloseMs: Number(options.autoCloseMs) || 0
        });
    };

    const closePopup = () => {
        setPopup({ ...popup, show: false, autoCloseMs: 0 });
    };

    const toggleFieldVisibility = (fieldName) => {
        setShowSensitiveFields((prev) => ({
            ...prev,
            [fieldName]: !prev[fieldName]
        }));
    };

    const closeLegalModal = () => {
        setLegalModal({ open: false, url: '', title: '', isLocal: false });
    };

    const openLegalLink = async (e, url, title) => {
        e.preventDefault();
        e.stopPropagation();

        setLegalModal({
            open: true,
            url,
            title,
            isLocal: url.startsWith('/')
        });
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;

        if ((name === 'consent' || name === 'consentTerms') && type === 'checkbox') {
            setFormData((prev) => ({ ...prev, [name]: checked }));
            return;
        }

        if (name === 'mobile' || name === 'mobileOtp') {
            const val = value.replace(/\D/g, '');
            if (name === 'mobile') {
                const nextMobile = val.slice(0, 10);
                if (nextMobile.length > 0 && !/^[6-9]/.test(nextMobile)) {
                    setFormData((prev) => ({
                        ...prev,
                        mobile: '',
                        mobileOtp: ''
                    }));
                    setOtpSent((prev) => ({ ...prev, mobile: false }));
                    setOtpCooldown(0);
                    return;
                }
                setFormData((prev) => ({
                    ...prev,
                    mobile: nextMobile,
                    mobileOtp: ''
                }));
                setOtpSent((prev) => ({ ...prev, mobile: false }));
                setOtpCooldown(0);
                setShowPanFallback(true);
            } else {
                setFormData({ ...formData, [name]: val.slice(0, 4) });
            }
        } else if (name === 'panNumber') {
            const panVal = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
            setFormData((prev) => ({ ...prev, panNumber: panVal, mobileOtp: '' }));
            setOtpSent((prev) => ({ ...prev, mobile: false }));
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSendOtp = async (type) => {
        if (type === 'mobile') {
            if (!String(formData.mobile || '').trim()) {
                showPopup("Please enter your mobile number.", "error");
                return;
            }
            if (!isValidMobileNumber(formData.mobile)) {
                showPopup("Please provide a valid mobile number.", "error");
                return;
            }
            if (!String(formData.panNumber || '').trim()) {
                showPopup("Please enter your PAN number before requesting OTP.", "error");
                return;
            }
            if (!isValidPanNumber(formData.panNumber)) {
                showPopup("Please enter a valid PAN number before requesting OTP.", "error");
                return;
            }
            if (!formData.consent) {
                showPopup("Please provide consent before requesting OTP.", "error");
                return;
            }
            if (!formData.consentTerms) {
                showPopup("Please accept Terms & Conditions and Privacy Policy before requesting OTP.", "error");
                return;
            }
        }

        if (otpCooldown > 0) return;

        setIsSendingOtp(true);
        const baseUrl = getApiBaseUrl();
        try {
            const response = await fetch(`${baseUrl}/api/send-otp`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(
                    type === 'mobile'
                        ? { mobile: formData.mobile, purpose: 'login' }
                        : { mobile: formData.mobile, purpose: 'login' } // Fallback, though type is 'mobile'
                )
            });

            let data;
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
                data = await response.json();
            } else {
                const text = await response.text();
                showPopup(`HTTP ${response.status}: ${text.slice(0, 50)}`, "error");
                return;
            }

            if (response.ok) {
                const message = data.devOtp
                    ? `OTP generated for testing: ${data.devOtp}`
                    : "OTP sent successfully to your mobile number.";
                showPopup(message, "success", { autoCloseMs: 2000 });
                setFormData((prev) => ({
                    ...prev,
                    mobileOtp: ''
                }));
                setOtpSent({ ...otpSent, [type]: true });
                setShowPanFallback(true);
                setOtpCooldown(showPanFallback ? 60 : 30);
            } else {
                showPopup(data.message || "Failed to send OTP.", "error");
            }
        } catch (error) {
            console.error("OTP Error:", error);
            if (error instanceof TypeError) {
                showPopup(`Cannot connect to API server at ${baseUrl || 'current origin'}. Start backend and try again.`, "error");
            } else {
                showPopup("Server error. Please try again later.", "error");
            }
        } finally {
            setIsSendingOtp(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (!formData.consent) {
            showPopup("Please provide consent to continue.", "error");
            return;
        }
        if (!formData.consentTerms) {
            showPopup("Please accept Terms & Conditions and Privacy Policy to continue.", "error");
            return;
        }

        if (!String(formData.mobile || '').trim()) {
            showPopup("Please enter your mobile number.", "error");
            return;
        }
        if (!isValidMobileNumber(formData.mobile)) {
            showPopup("Please provide a valid mobile number.", "error");
            return;
        }
        if (!otpSent.mobile) {
            showPopup("Please send OTP first.", "error");
            return;
        }
        if (!String(formData.mobileOtp || '').trim()) {
            showPopup("Please enter the OTP sent to your mobile number.", "error");
            return;
        }
        if (String(formData.mobileOtp || '').trim().length !== 4) {
            showPopup("Please enter a valid 4-digit OTP.", "error");
            return;
        }
        if (!String(formData.panNumber || '').trim()) {
            showPopup("Please enter your PAN number to continue.", "error");
            return;
        }
        if (!isValidPanNumber(formData.panNumber)) {
            showPopup("Please enter a valid PAN number.", "error");
            return;
        }
        setIsSubmitting(true);
        let keepSubmitting = false;
        try {
            const baseUrl = getApiBaseUrl();
            const response = await fetch(`${baseUrl}/api/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mobile: formData.mobile,
                    otp: formData.mobileOtp,
                    location: location,
                    panNumber: formData.panNumber,
                    kioskId: localStorage.getItem('kiosk_id') || null
                })
            });

            let data;
            const contentType = response.headers.get("content-type");
            if (contentType && contentType.indexOf("application/json") !== -1) {
                data = await response.json();
            } else {
                const text = await response.text();
                showPopup(`HTTP ${response.status}: ${text.slice(0, 50)}`, "error");
                return;
            }

            if (response.ok) {
                setShowPanFallback(false);
                if (data.user || data.token || data.isNewUser) {
                    clearUserSession();
                    persistLoginSession(data.user, data.token);
                    pingKioskLogin(
                        formData.mobile,
                        String(data.user?.full_name || data.user?.name || '').trim()
                    );
                    // showPopup(congratsMsg, "success");
                    keepSubmitting = true;
                    setIsSubmitting(false);
                    navigate('/dashboard', {
                        state: withRouteLoaderState({}, 'Opening your dashboard...')
                    });
                    return;
                }
            } else if (data?.error === 'INVALID_PAN_NUMBER') {
                showPopup("Invalid PAN number", "error", { autoCloseMs: 2000 });
            } else if (data?.error === 'PAN_LOOKUP_UNAVAILABLE' || response.status === 503) {
                setShowPanFallback(true);
                showPopup(normalizeLoginErrorMessage(data.message), "error", { autoCloseMs: 3000 });
            } else {
                showPopup(normalizeLoginErrorMessage(data.message), "error");
            }
        } catch (error) {
            console.error("Login Error:", error);
            showPopup("Server connection failed.", "error");
        } finally {
            if (!keepSubmitting) {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <div className="login-page">
            {entryLoaderMessage && <FullPageLoader message={entryLoaderMessage} />}
            {/* Custom Popup Modal */}
            {popup.show && (
                <div className="popup-overlay">
                    <div className={`popup-box ${popup.type}`}>
                        <div className="popup-icon">
                            {popup.type === 'success' ? '✓' : popup.type === 'error' ? '✕' : '!'}
                        </div>
                        <p>{popup.message}</p>
                        {!popup.autoCloseMs && <button onClick={closePopup}>OK</button>}
                    </div>
                </div>
            )}

            {isSubmitting && (
                <FullPageLoader message="Verifying your details..." />
            )}

            {legalModal.open && (
                <div className="legal-modal-overlay">
                    <div className="legal-modal">
                        <div className="legal-modal-header">
                            <strong>{legalModal.title}</strong>
                            <button
                                type="button"
                                className="legal-modal-close"
                                onClick={closeLegalModal}
                                aria-label="Close legal page"
                            >
                                ×
                            </button>
                        </div>
                        <div className={`legal-modal-body${legalModal.isLocal ? ' legal-modal-body--local' : ''}`}>
                            <iframe
                                src={legalModal.url}
                                title={legalModal.title}
                                className={`legal-modal-iframe${legalModal.isLocal ? ' legal-modal-iframe--local' : ''}`}
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* <div className="login-carousel-container">
                <Carousel slides={slides} interval={3500} />
            </div> */}

            <div className="login-form-container">
                <div className="form-wrapper">
                    <p className="brand-tag">FinnCube Credit Services Pvt. Ltd.</p>
                    <h2>Get Started</h2>
                    <p className="subtitle">Check your loan limit instantly.</p>

                    <form onSubmit={handleSubmit} autoComplete="off">
                        {/* Common Fields: Mobile Number */}
                        <div className="form-group" style={{ marginTop: '20px' }}>
                            <label htmlFor="mobile" style={{ marginBottom: '15px', color: '#000', fontSize: '1.45rem', fontWeight: 700 }}>Mobile Number <span className="required">*</span></label>
                            <div className="input-with-toggle">
                                <input
                                    type={showSensitiveFields.mobile ? 'text' : 'password'}
                                    id="mobile"
                                    name="mobile"
                                    placeholder="9876543210"
                                    value={formData.mobile}
                                    onChange={handleChange}
                                    required
                                    maxLength="10"
                                    inputMode="none"
                                    data-layout="numeric"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck="false"
                                />
                                <button
                                    type="button"
                                    className="visibility-toggle-btn"
                                    onClick={() => toggleFieldVisibility('mobile')}
                                    aria-label={showSensitiveFields.mobile ? 'Hide mobile number' : 'Show mobile number'}
                                    aria-pressed={showSensitiveFields.mobile}
                                >
                                    <VisibilityIcon visible={showSensitiveFields.mobile} />
                                </button>
                            </div>
                        </div>
                        
                        <div className="form-group slide-down">
                            <label htmlFor="panNumber" className="mobile-otp-label">PAN Number <span className="required">*</span></label>
                            <div className="input-with-toggle">
                                <input
                                    type="text"
                                    id="panNumber"
                                    name="panNumber"
                                    className={`mobile-otp-input ${showSensitiveFields.panNumber ? '' : 'masked-pan-input'}`}
                                    placeholder="ABCDE1234F"
                                    value={formData.panNumber}
                                    onChange={handleChange}
                                    required
                                    maxLength="10"
                                    inputMode="none"
                                    data-layout="alpha-numeric"
                                    autoComplete="off"
                                    autoCorrect="off"
                                    autoCapitalize="characters"
                                    spellCheck="false"
                                    ref={panFallbackRef}
                                />
                                <button
                                    type="button"
                                    className="visibility-toggle-btn"
                                    onClick={() => toggleFieldVisibility('panNumber')}
                                    aria-label={showSensitiveFields.panNumber ? 'Hide PAN number' : 'Show PAN number'}
                                    aria-pressed={showSensitiveFields.panNumber}
                                >
                                    <VisibilityIcon visible={showSensitiveFields.panNumber} />
                                </button>
                            </div>
                        </div>

                        <div className="form-group" style={{ marginTop: '-4px' }}>
                            <label style={{ marginBottom: '15px', color: '#000', fontSize: '1.45rem', fontWeight: 700 }}>Consent <span className="required">*</span></label>
                            <label htmlFor="consent" style={{ display: 'flex', alignItems: 'center', gap: '16px', color: '#000', fontSize: '1.35rem', cursor: 'pointer', lineHeight: 1.6 }}>
                                <input
                                    type="checkbox"
                                    id="consent"
                                    name="consent"
                                    checked={formData.consent}
                                    onChange={handleChange}
                                    style={{ display: 'none' }}
                                />
                                <span
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        border: '2px solid #64748b',
                                        borderRadius: '4px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: formData.consent ? '#16a34a' : '#fff',
                                        color: '#fff',
                                        fontWeight: '800',
                                        fontSize: '24px',
                                        lineHeight: 1
                                    }}
                                >
                                    {formData.consent ? '✓' : ''}
                                </span>
                                I consent to data processing and credit assessment.
                            </label>
                            <label htmlFor="consentTerms" style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', color: '#000', fontSize: '1.35rem', cursor: 'pointer', lineHeight: 1.6, marginTop: '12px' }}>
                                <input
                                    type="checkbox"
                                    id="consentTerms"
                                    name="consentTerms"
                                    checked={formData.consentTerms}
                                    onChange={handleChange}
                                    style={{ display: 'none' }}
                                />
                                <span
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        border: '2px solid #64748b',
                                        borderRadius: '4px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: formData.consentTerms ? '#16a34a' : '#fff',
                                        color: '#fff',
                                        fontWeight: '800',
                                        fontSize: '24px',
                                        lineHeight: 1,
                                        marginTop: '2px',
                                        flexShrink: 0
                                    }}
                                >
                                    {formData.consentTerms ? '✓' : ''}
                                </span>
                                <span>
                                    <span>
                                        I agree to the <a href="/terms-and-conditions" className="consent-link" onClick={(e) => openLegalLink(e, '/terms-and-conditions', 'Terms & Conditions')}>Terms &amp; Conditions</a> and <a href="/privacy-policy" className="consent-link" onClick={(e) => openLegalLink(e, '/privacy-policy', 'Privacy Policy')}>Privacy Policy</a>.
                                    </span>
                                </span>
                            </label>
                        </div>

                        {!otpSent.mobile && (
                            <div className="form-group">
                                <button
                                    type="button"
                                    className="secondary-btn send-otp-btn"
                                    style={{ width: '100%', borderRadius: '12px' }}
                                    onClick={() => handleSendOtp('mobile')}
                                    disabled={
                                        isSendingOtp
                                        || otpCooldown > 0
                                    }
                                >
                                    {isSendingOtp
                                        ? 'Sending OTP...'
                                        : otpCooldown > 0
                                            ? `Resend OTP in ${otpCooldown}s`
                                            : 'Send OTP'}
                                </button>
                            </div>
                        )}

                        {otpSent.mobile && (
                            <div className="form-group slide-down">
                                <label htmlFor="mobileOtp" className="mobile-otp-label">Verify Mobile OTP <span className="required">*</span></label>
                                <div className="otp-action-row">
                                    <input
                                        type="text"
                                        id="mobileOtp"
                                        name="mobileOtp"
                                        className="mobile-otp-input"
                                        placeholder="Enter 4-digit OTP"
                                        value={formData.mobileOtp}
                                        onChange={handleChange}
                                        required
                                        maxLength="4"
                                        inputMode="none"
                                        data-layout="numeric"
                                        autoComplete="one-time-code"
                                    />
                                    <button
                                        type="button"
                                        className="secondary-btn otp-resend-btn"
                                        onClick={() => handleSendOtp('mobile')}
                                        disabled={isSendingOtp || otpCooldown > 0}
                                    >
                                        {isSendingOtp
                                            ? 'Sending OTP...'
                                            : otpCooldown > 0
                                                ? `Resend OTP in ${otpCooldown}s`
                                                : 'Resend OTP'}
                                    </button>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="login-btn"
                            style={{ marginTop: '10px' }}
                            disabled={isSubmitting}
                            aria-busy={isSubmitting}
                        >
                            {isSubmitting ? (
                                <span className="login-btn__content">
                                    <span className="login-btn__spinner" aria-hidden="true"></span>
                                    Verifying...
                                </span>
                            ) : 'Continue'}
                        </button>

                        <div className="form-footer">
                            <div className="forgot-password">
                                <Link to="/trouble-login" className="trouble-link">Trouble Logging in?</Link>
                            </div>
                        </div>

                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <Link to="/" className="back-link">← Back to Start</Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
